import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Calendar, MapPin, Users, Clock, ArrowRight, Download } from 'lucide-react';
import { events } from '../mockData';
import { RegistrationModal } from './RegistrationModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Events = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventCapacities, setEventCapacities] = useState({});

  // Fetch real-time capacities for all events
  useEffect(() => {
    const fetchCapacities = async () => {
      const capacities = {};
      for (const event of events) {
        try {
          const response = await axios.get(`${API}/event-capacity/${encodeURIComponent(event.title)}`);
          capacities[event.title] = response.data.registered_count;
        } catch (error) {
          console.error(`Error fetching capacity for ${event.title}:`, error);
          capacities[event.title] = 0;
        }
      }
      setEventCapacities(capacities);
    };

    fetchCapacities();
    // Refresh capacities every 30 seconds
    const interval = setInterval(fetchCapacities, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRegister = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleRegistrationComplete = () => {
    // Refresh capacities after registration
    const fetchCapacities = async () => {
      const capacities = {};
      for (const event of events) {
        try {
          const response = await axios.get(`${API}/event-capacity/${encodeURIComponent(event.title)}`);
          capacities[event.title] = response.data.registered_count;
        } catch (error) {
          capacities[event.title] = 0;
        }
      }
      setEventCapacities(capacities);
    };
    fetchCapacities();
  };

  const handleExportRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/export-registrations`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tgs_registrations.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting registrations:', error);
    }
  };

  return (
    <section id="events" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#FAF8F6]">
      <div className="max-w-7xl mx-auto">
        {/* Registration Modal */}
        <RegistrationModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            handleRegistrationComplete();
          }}
          eventTitle={selectedEvent?.title}
        />
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Upcoming Events
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Join Telfer Gateway Society for networking events, panels, and workshops that connect you with 
            industries beyond traditional business sectors.
          </p>
          
          {/* Export Button */}
          <div className="mt-6">
            <Button
              onClick={handleExportRegistrations}
              variant="outline"
              className="border-2 border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538] hover:text-white"
            >
              <Download className="mr-2 h-4 w-4" />
              Export All Registrations
            </Button>
          </div>
        </div>

        {/* Event Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {events.map((event) => {
            const realCapacity = eventCapacities[event.title] || 0;
            const totalCapacity = event.attendees + event.spotsLeft;
            const spotsLeft = Math.max(0, totalCapacity - realCapacity);
            const percentFilled = totalCapacity > 0 ? Math.round((realCapacity / totalCapacity) * 100) : 0;
            
            // Show "Coming Soon" if no registrations yet
            const showComingSoon = realCapacity === 0;
            
            return (
              <Card
                key={event.id}
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-slate-200 hover:border-[#8B1538]/30 bg-white"
              >
                {/* Event Header */}
                <div className="bg-gradient-to-br from-[#8B1538] to-[#6B1028] p-6 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="h-5 w-5" />
                      <span className="font-semibold">{event.date}</span>
                    </div>
                    <h3 className="text-xl font-bold leading-tight">{event.title}</h3>
                  </div>
                </div>

                {/* Event Body */}
                <div className="p-6">
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3 text-slate-600">
                      <Clock className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-start gap-3 text-slate-600">
                      <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <span>{event.location}</span>
                    </div>
                    <div className="flex items-start gap-3 text-slate-600">
                      <Users className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      {showComingSoon ? (
                        <span className="font-semibold text-[#8B1538]">Registration Opening Soon</span>
                      ) : (
                        <span>{realCapacity} registered • {spotsLeft} spots left</span>
                      )}
                    </div>
                  </div>

                  <p className="text-slate-700 mb-6 leading-relaxed">{event.description}</p>

                  {/* Progress Bar - Only show if there are registrations */}
                  {!showComingSoon && (
                    <div className="mb-6">
                      <div className="flex justify-between text-sm text-slate-600 mb-2">
                        <span>Capacity</span>
                        <span>{percentFilled}% filled</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-[#8B1538] h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentFilled}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Register Button */}
                  <Button
                    onClick={() => handleRegister(event)}
                    className="w-full group transition-all duration-300 bg-[#8B1538] hover:bg-[#6B1028] text-white"
                  >
                    Register Now
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="inline-block p-8 sm:p-10 bg-gradient-to-br from-[#8B1538] to-[#6B1028] border-0 shadow-lg max-w-3xl text-white">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              Want to Stay Updated?
            </h3>
            <p className="text-lg text-white/90 mb-6">
              Get notified about upcoming Telfer Gateway Society events, networking opportunities, and exclusive industry connections.
            </p>
            <Button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              size="lg"
              className="bg-white text-[#8B1538] hover:bg-slate-50 px-8 py-6 text-lg font-semibold shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 group"
            >
              Join Our Community
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Card>
        </div>
      </div>
    </section>
  );
};
